import React, { ReactNode, useEffect } from 'react';

import { createUseStyles } from 'react-jss';

import Header, { HeaderProps } from 'DesignSystem2/Header/Header';
import { useStores } from 'hooks/use-stores';

import { Breakpoint, Palette, Radius, Spacing } from '../Theme';

const useStyles = createUseStyles({
    '@keyframes show': { from: { opacity: 0 }, to: { opacity: 1 } },
    '@keyframes slideIntoPosition': { from: { transform: 'translateY(24px)' }, to: { transform: 'none' } },
    background: {
        zIndex: 1000,
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        display: 'flex',
        justifyContent: 'center',
        top: 0,
        left: 0,
        backgroundColor: Palette.Surface.Overlay,
        color: Palette.Text.x10,
        animation: '$show 0.1s ease forwards',
    },
    modal: {
        opacity: 0,
        animation: '$show 0.3s ease 0.1s forwards, $slideIntoPosition 0.3s ease 0.1s forwards',
        boxSizing: 'border-box',
        top: Spacing(7),
        position: 'relative',
        maxWidth: `calc(100vw - ${Spacing(2)} - ${Spacing(2)})`,
        height: 'fit-content',
        maxHeight: `calc(100vh - ${Spacing(8)} - ${Spacing(2)})`,
        borderRadius: Radius.x40,
        backgroundColor: Palette.Surface.x10,
        [Breakpoint.down.sm]: {
            marginTop: 0,
            animation: '$show 0.3s ease forwards, $slideIntoPosition 0.3s ease forwards',
            borderRadius: 0,
            height: '100vh',
            maxHeight: 'none',
            maxWidth: 'none',
        },
    },
    dialog: {
        opacity: 0,
        animation: '$show 0.3s ease 0.1s forwards, $slideIntoPosition 0.3s ease 0.1s forwards',
        boxSizing: 'border-box',
        alignSelf: 'center',
        maxWidth: `calc(100vw - ${Spacing(2)} - ${Spacing(2)})`,
        height: 'fit-content',
        maxHeight: `calc(100vh - ${Spacing(2)} - ${Spacing(2)})`,
        borderRadius: Radius.x40,
        backgroundColor: Palette.Surface.x10,
    },
});

//TODO: @dparikh resolve the deprecation
type ModalVariant = 'small' | 'medium' | 'mid-large' | 'large' | 'dialog';

interface ModalProps {
    headerProps?: HeaderProps;
    headerContent?: JSX.Element;
    variant?: ModalVariant;
    smallPadding?: boolean;
    modalWidth?: number | string;
    open: boolean;
    onClickBackground?: () => void;
    children: ReactNode;
    styles?: React.CSSProperties;
    backgroundStyles?: React.CSSProperties;
}

const Modal = ({
    headerProps = undefined,
    headerContent = undefined,
    open = false,
    variant = 'medium',
    smallPadding = false,
    modalWidth,
    onClickBackground = undefined,
    children,
    styles,
    backgroundStyles,
}: ModalProps): JSX.Element => {
    const classes = useStyles();

    const { uiStore } = useStores();

    const isSM = Breakpoint.isWidthDown.sm();

    useEffect(() => {
        if (open) {
            uiStore.lockScroll();
        } else {
            uiStore.unlockScroll();
        }
    }, [open]);

    // Always unlock scrolling on unmount
    useEffect(() => {
        return (): void => {
            uiStore.unlockScroll();
        };
    }, []);

    if (!open) {
        return <></>;
    }

    let header = null;
    if (!!headerProps) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { compact, ...modalHeaderProps } = headerProps;
        header = <Header {...modalHeaderProps} headerContent={headerContent} hideBottomBorder compact center />;
    }

    const width = !!modalWidth
        ? modalWidth
        : variant === 'dialog'
        ? 340
        : isSM
        ? '100vw'
        : variant === 'small'
        ? 480
        : variant === 'medium'
        ? 640
        : variant === 'mid-large'
        ? 820
        : 1000;
    const defaultPadding = isSM || smallPadding ? 16 : 24;

    return (
        <div className={classes.background} onClick={!!onClickBackground ? onClickBackground : undefined}>
            <div
                className={variant === 'dialog' ? classes.dialog : classes.modal}
                style={{ display: 'flex', flexDirection: 'column', width, ...backgroundStyles }}
                onClick={(e): void => e.stopPropagation()}
            >
                {header}
                <div
                    className={
                        'mr-[6px] max-h-[calc(100vh-150px)] overflow-y-auto [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-fill-x50 [&::-webkit-scrollbar-track]:rounded-x50 [&::-webkit-scrollbar-thumb]:bg-fill-x30 [&::-webkit-scrollbar-thumb]:rounded-x50'
                    }
                    style={{
                        paddingLeft: defaultPadding,
                        paddingRight: defaultPadding - 6, // -6 to account for the scrollbar margin right
                        marginTop: !!header ? Spacing(2) : defaultPadding,
                        marginBottom: isSM && variant !== 'dialog' ? Spacing(12) : defaultPadding,
                        scrollbarGutter: 'stable both-edges',
                        ...styles,
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Modal;
